import { applyFilterEntryToWorkQBody, applySavedWorkQueueView, checkAndSetExceptionsForWorkQs, checkAndSetFiltersForWorkQsV2, checkAndSetFiltersForWorkQsV2Persistent, computeWorkQDefaultFiltersFromConditions, mergeEnforceFiltersIntoView } from "./filters"
import { filterActions } from "../store/filter"
import { getItemFromStorageSimple, isStateOfPip } from "./script"
import { attorneyStatusUnattached, caseSourcesOptions, caseTypeOptions, frontendOptions, gdqStatusOptions, liabilityPolicyStatusOptionsPayerTypes, majorMedPolicyStatusOptionsPayerTypes, medPayMPPolicyStatusOptionsPayerTypes, medPayPIPPolicyStatusOptionsPayerTypes, medPayPolicyLimitsOptions, resolveAttorneyStatusOption, retrieveAllPayerStatuses, retrievePayerTypes, treatmentStatusesOptions, umPolicyStatusOptionsPayerTypes, workerCompsATPPolicyStatusOptionsPayerTypes, getCaseLevelOptions, practicesOptions, MRQStatusesOptions, practicePatientStatusesOptions } from "./systemData"
import { parseApiDateOrDateTime } from "./dateParsing"

/**
 * Ensure work queue `grouped_fields` is a sequential array of groups with array `items`.
 * Preserves API / Nova `_order` when Laravel returns a list, and recovers when sparse
 * keys were JSON-encoded as an object.
 *
 * @param {unknown} groupedFields
 * @returns {Array<{ groupName?: string, items: array }>}
 */
export function normalizeGroupedFields(groupedFields) {
    if (!groupedFields) {
        return []
    }

    const groups = Array.isArray(groupedFields)
        ? groupedFields
        : (typeof groupedFields === "object" ? Object.values(groupedFields) : [])

    return groups
        .filter((group) => group && typeof group === "object")
        .map((group) => {
            const rawItems = group.items
            const items = Array.isArray(rawItems)
                ? rawItems
                : (rawItems && typeof rawItems === "object" ? Object.values(rawItems) : [])

            return {
                ...group,
                items: items.filter((item) => item && typeof item === "object"),
            }
        })
        .filter((group) => Array.isArray(group.items))
}

/**
 * @param {{ grouped_fields?: unknown } | null | undefined} workq
 * @returns {Array<{ groupName?: string, items: array }>}
 */
export function getWorkQGroupedFields(workq) {
    return normalizeGroupedFields(workq?.grouped_fields)
}

export const WORKQ_LIABILITY_LIMITS_CONFIRMED_KEY = 'liability_limits_confirmed_with_carrier'

/**
 * Hide permission-gated WorkQ columns for the current user.
 *
 * @param {Array<{ groupName?: string, items: array }>} groupedFields
 * @param {Record<string, boolean> | null | undefined} editPermissions
 * @returns {Array<{ groupName?: string, items: array }>}
 */
export function filterWorkQGroupedFieldsByPermission(groupedFields, editPermissions) {
    if (!Array.isArray(groupedFields) || groupedFields.length === 0) {
        return []
    }

    if (editPermissions?.liability_confirmed_with_carrier) {
        return groupedFields
    }

    return groupedFields
        .map((group) => ({
            ...group,
            items: (group.items || []).filter((item) => item?.key !== WORKQ_LIABILITY_LIMITS_CONFIRMED_KEY),
        }))
        .filter((group) => group.items.length > 0)
}

/**
 * @param {{ grouped_fields?: unknown } | null | undefined} workq
 * @param {Record<string, boolean> | null | undefined} editPermissions
 * @returns {Array<{ groupName?: string, items: array }>}
 */
export function getWorkQGroupedFieldsForUser(workq, editPermissions) {
    return filterWorkQGroupedFieldsByPermission(getWorkQGroupedFields(workq), editPermissions)
}

function buildCurrentUserCollectionsFilterOption(user) {
    if (!user?.id) {
        return null
    }

    const fallbackName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
    const label = user?.name || fallbackName || user?.email || user?.username
    if (!label) {
        return null
    }

    return {
        value: user.id,
        label: label,
        item: {
            id: user.id,
            value: user.id,
            label: label,
            tag: 'collections'
        },
        async: true
    }
}

export function getCollectionRepDismissedMap() {
    if (typeof window === 'undefined') {
        return {}
    }

    if (!window.__collectionRepDismissedByWorkQ) {
        window.__collectionRepDismissedByWorkQ = {}
    }

    return window.__collectionRepDismissedByWorkQ
}

export function getCollectionRepDismissedForWorkQ(workqSlug) {
    if (typeof window === 'undefined' || !workqSlug) {
        return false
    }

    return !!getCollectionRepDismissedMap()?.[workqSlug]
}

export function markCollectionRepDismissed(workqSlug) {
    if (typeof window === 'undefined' || !workqSlug) {
        return
    }

    getCollectionRepDismissedMap()[workqSlug] = true
}

export function setBodyOnWorkQ(workq, workqSlug, initialLoadWorkq, reduxFilters, reduxExceptions, user, onPage, orderFilters, savedView, dispatch) {
    let body = {}           
    let filters = reduxFilters
    let exceptions = reduxExceptions

    const userLocations = getItemFromStorageSimple('userLocations')
     
    if (onPage) {
        body.page = onPage
    }
    
    // On a queue switch (initial load for this workq), never carry the previously viewed queue's
    // Redux filters/exceptions into this queue's request. Base them solely on this queue's persisted
    // localStorage, else its saved personal view, else its computed defaults, else empty. Carrying
    // them over made some Qs (e.g. Verifications Q, Unattached PI) show the wrong list until Reset.
    if (workq && (!initialLoadWorkq || (initialLoadWorkq != workqSlug))) {
        let appliedSavedViewExceptions = false
        if (getItemFromStorageSimple(workqSlug)) {
            filters = {
                ...checkAndSetFiltersForWorkQsV2Persistent(workqSlug, dispatch)
            }
        } else {
            if (savedView && typeof savedView === 'object') {
                const applied = applySavedWorkQueueView(workqSlug, savedView, workq, dispatch)
                filters = { ...(applied?.filters || {}) }
                exceptions = applied?.exceptions || {}
                appliedSavedViewExceptions = true
            } else if (workq.filter_conditions) {
                const defaultFilters = computeWorkQDefaultFiltersFromConditions(workq.filter_conditions, userLocations)
                if (dispatch) {
                    dispatch(filterActions.setFiltersMultiple(defaultFilters))
                    dispatch(filterActions.setActiveSavedViewSlug(null))
                }
                filters = { ...defaultFilters }
            } else {
                filters = {}
                if (dispatch) {
                    dispatch(filterActions.setFiltersMultiple({}))
                    dispatch(filterActions.setActiveSavedViewSlug(null))
                }
            }
        }

        // Recompute exceptions from a clean base too, so a previous queue's exceptions don't leak.
        // Saved-view apply already restored exceptions for this slug.
        if (!appliedSavedViewExceptions) {
            const appliedExceptions = checkAndSetExceptionsForWorkQs(workq.exceptions_filters, dispatch, null)
            exceptions = appliedExceptions || {}
        }
    }   

    if (orderFilters) {
        body.order_by = `${orderFilters.group}.${orderFilters.label}`
        body.order_by_direction = orderFilters.isDesc ? 'desc' : 'asc'
    }

    const hasCollectionsRole = user?.roles?.some(role => role?.slug === 'collections')
    const hasCollectionsRepFilter = workq?.filter_groups?.some(filter => filter?.key === 'attorney_collection_rep')
    const hasCollectionRepSelected = Array.isArray(filters?.attorney_collection_rep) && filters.attorney_collection_rep.length
    const collectionsRepDismissed = getCollectionRepDismissedForWorkQ(workqSlug)

    if (!collectionsRepDismissed && hasCollectionsRole && hasCollectionsRepFilter && !hasCollectionRepSelected) {
        const defaultCollectionRep = buildCurrentUserCollectionsFilterOption(user)
        if (defaultCollectionRep) {
            filters = {
                ...filters,
                attorney_collection_rep: [defaultCollectionRep]
            }
        }
    }

    filters = mergeEnforceFiltersIntoView(filters, workq)

    if (filters) {    // && initialLoadWorkq      
        for (const [key, value] of Object.entries(filters)) { 
            if (applyFilterEntryToWorkQBody(body, key, filters)) {
                continue
            }
            if (key != 'payer_type_filters' && key != 'practice_patient_status_filters') {
                if ((typeof(value) == "number") || value) {
                    if ((typeof(filters[key]) == "number") || filters[key]) {
                        if (filters[key].constructor == Array) { // check if is array
                            let combinedValues = ''

                            if (filters[key].some(item => item.async)) {
                                filters[key].forEach((item, index) => {
                                combinedValues = combinedValues + item?.value + (index < filters[key].length-1 ? ',' : '')
                            })
                            } else {
                                filters[key].forEach((item, index) => {
                                // combinedValues = combinedValues + item?.value + (index < filters[key].length-1 ? ',' : '')
                                combinedValues = combinedValues + item + (index < filters[key].length-1 ? ',' : '')
                            })
                            }

                            body[key] = combinedValues
                        }  else {  
                            body[key] = filters[key]
                        }
                        
                    }
                }
            } else {
                body[key] = filters[key]
            }        
            
        }
        
    }     

    if (exceptions) { // && initialLoadWorkq
        for (const [key, value] of Object.entries(exceptions)) {                    
            if (value) {
                if (exceptions[key]) {
                    body[key] = exceptions[key]                           
                    
                }
            }
        }
    }


    return body
}


export function setBodyOnWorkQDashboard(workq, workqSlug) {
    let body = {}           
    let filters = null
    let exceptions = null

    const userLocations = getItemFromStorageSimple('userLocations')    

    
    if (workq && workq.filter_conditions) {    
        // , user?.locations   
        //initial loading !!!!!!!!!!!!    
        if (getItemFromStorageSimple(workqSlug)) {

            filters = {
                ...checkAndSetFiltersForWorkQsV2Persistent(workqSlug, null)
            } 
        } else {
            filters = {
                ...checkAndSetFiltersForWorkQsV2(workq.filter_conditions, null , filters, userLocations)
            } 
        }

   
        exceptions = checkAndSetExceptionsForWorkQs(workq.exceptions_filters, null , exceptions)

    }  
    if (filters) {    // && initialLoadWorkq           
        for (const [key, value] of Object.entries(filters)) { 
            if (applyFilterEntryToWorkQBody(body, key, filters)) {
                continue
            }
            if (key != 'payer_type_filters' && key != 'practice_patient_status_filters') {
                if ((typeof(value) == "number") || value) {
                    if ((typeof(filters[key]) == "number") || filters[key]) {
                        if (filters[key].constructor == Array) { // check if is array
                            let combinedValues = ''

                            if (filters[key].some(item => item.async)) {
                                filters[key].forEach((item, index) => {
                                combinedValues = combinedValues + item?.value + (index < filters[key].length-1 ? ',' : '')
                            })
                            } else {
                                filters[key].forEach((item, index) => {
                                // combinedValues = combinedValues + item?.value + (index < filters[key].length-1 ? ',' : '')
                                combinedValues = combinedValues + item + (index < filters[key].length-1 ? ',' : '')
                            })
                            }

                            body[key] = combinedValues
                        }  else {  
                            body[key] = filters[key]
                        }
                        
                    }
                }
            } else {
                body[key] = filters[key]
            }        
            
        }
        
    }     

    if (exceptions) { // && initialLoadWorkq
        for (const [key, value] of Object.entries(exceptions)) {                    
            if (value) {
                if (exceptions[key]) {
                    body[key] = exceptions[key]                           
                    
                }
            }
        }
    }

    return body
}

export function statusCellWorkQ(itemKey, caseData, data, itemData) {
    if (itemKey == 'attorney_status') {
        // Backend sends attorney.status_id = active managing attorney's status_id when one exists,
        // otherwise case_additionals.no_attorney_reason. Route the lookup to the correct list:
        // - case has a managing attorney -> show_in_attorney_status options
        // - case has no managing attorney -> show_in_no_atty_reason options
        // Detect "has managing attorney" by the presence of `caseData.attorney.id` (only sent when
        // there is an active managing attorney), with a fallback that probes each list in turn.
        const hasManagingAttorney = !!caseData?.attorney?.id

        if (data != null) {
            const item = resolveAttorneyStatusOption(data, hasManagingAttorney)
            if (item) {
                return item
            }
        } else if (caseData?.additional?.no_attorney_reason) {
            let item = attorneyStatusUnattached()?.find(item => item.value == caseData.additional.no_attorney_reason)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'liability_status') {
        let options = frontendOptions()?.assumedOptions

        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }

    if (itemKey == 'liability_policy_status') {
        let options = liabilityPolicyStatusOptionsPayerTypes()

        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }        

    if (itemKey == 'medpay_status') {
        // let options = medpayRadioFields
        let options = medPayMPPolicyStatusOptionsPayerTypes()

        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return {
                    ...item,
                    is_exhausted: caseData?.mp_pip?.mp_exausted
                }
            }

        }
    }

    if (itemKey == 'medpay_pip_status') {

        if (isStateOfPip(caseData?.mp_pip?.insurance_state ?? caseData?.mp_pip_um?.find(r => r.is_primary_insurance)?.insurance_state)) {
            let options = medPayPIPPolicyStatusOptionsPayerTypes()
    
            if (options) {
                let item = options.find(item => item.value == data)
                if (item) {
                    return {
                        ...item,
                        is_exhausted: caseData?.mp_pip?.pip_exausted
                    }
                }
    
            }
        } else {
            // setNoDataCell(true) ?????????????????????????
            return null
        }
        
    }

    if (itemKey == 'medpay_pip_limit') {
        let options = medPayPolicyLimitsOptions()
    
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }        

    if (itemKey == 'has_um') {
        let options = frontendOptions()?.umHeaderRadioFields

        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }        

    if (itemKey == 'um_status') {
        let options = umPolicyStatusOptionsPayerTypes()
    
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }

    if (itemKey == 'major_med_policy_status') {
        let options = majorMedPolicyStatusOptionsPayerTypes()
    
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }

    if (
        itemKey == 'work_comp_policy_status' ||
        itemKey == 'worker_comp_policy_status' ||
        (itemData?.group == 'worker_comp' && itemData?.label == 'policy_status')
    ) {
        let options = workerCompsATPPolicyStatusOptionsPayerTypes()

        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'case_hiof_option') {
        let options = frontendOptions()?.HIOF_Selected_Options
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }

    if (itemKey == 'case_data__master_status') {
        let options = frontendOptions()?.MASTER_STATUS
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }

    if (itemKey == 'case_data_gdq_status_status') {
        let options = gdqStatusOptions()
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }

    if (itemKey == 'speciality_status') {
        let options = treatmentStatusesOptions()
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }

        }
    }

    if (itemKey == 'case_data_payer_type') {
        let options = retrievePayerTypes()
        if (options) {
            let item = options.find(item => item.value == data)

            if (item) {
                return item
            } else {
                return {
                    label: 'Undetermined'
                }
            }

        }
    }

    if (itemKey == 'case_data_payer_status' || itemData?.label == 'payer_status_id') {
        let options = retrieveAllPayerStatuses()
        
        if (options) {
            let item = options.find(item => item.value == data)

            if (item) {
                return item
            } else {
                return {
                    label: 'Undetermined'
                }
            }

        }
    }
    
    if (itemKey == 'case_data_master_status') {
        let options = frontendOptions()?.MASTER_STATUS
        if (options) {
            let item = options.find(item => item.value == data)

            if (item) {
                return item
            }

        }
    }

    if (itemKey == 'case_data_type') {
        let options = caseTypeOptions()
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'case_data_level') {
        let options = getCaseLevelOptions()
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'case_data_source') {
        let options = caseSourcesOptions()
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'case_data_gen_treating_status') {

        let options = frontendOptions()?.GENERAL_TREATING_STATUS
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'case_practices_status') {
        // need to inject status for next gen
        let injectStatus = null
        if (!data && itemData?.nextgen_patient_status) {
            injectStatus = {
                label: itemData?.nextgen_patient_status,
                value: itemData?.nextgen_patient_status,
                manual_selection: true
            }
        }

        // testing purpose
        // if (!data) {
        //     injectStatus = {
        //         label: 'Next Gen',
        //         value: 'next_gen',
        //         manual_selection: true
        //     }
        // }
        // let options = treatmentStatusesOptions(false, injectStatus)
        let options = practicePatientStatusesOptions(false, injectStatus)
        if (options) {
            let item = options.find(item => item.value == (data || injectStatus?.value))
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'case_practices_mrq_status') {
        let options = MRQStatusesOptions()
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'case_gdq_staus_gdq_update') {
        let options = gdqStatusOptions()
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }


    if (itemKey == 'surgery_approval_status' || itemKey == 'surgery_approval_status_update') {
        let options = frontendOptions()?.surgeryApprovalStatus
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'surgery_surgical_status') {
        let options = frontendOptions()?.surgeryRecomandation
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

    if (itemKey == 'surgery_surgery_charges_status' || itemKey == 'surgery_surgery_charges_status_update') {
        let options = frontendOptions()?.surgeryChargedStatus
        if (options) {
            let item = options.find(item => item.value == data)
            if (item) {
                return item
            }
        }
    }

}


export const workQFunctions = {
    numberOfDaysSinceLastVisitHasPassed: (caseData) => {
        if (caseData && caseData.last_appointment_date) {
            const numberOfDays = 21
            const today = new Date()
            // const todayPlus5Days = new Date(today.getTime() + 5 * 24 * 60 * 60 * 1000) // for test only


            const compareDate = parseApiDateOrDateTime(caseData.last_appointment_date)
            if (!compareDate) return false
            const diffTime = Math.abs(today - compareDate)


            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
            if (diffDays >= numberOfDays) {
                return true
            }            
        }
        return false
    },
    numberOfDaysSinceinPendingDischarged: (caseData) => {
        if (caseData && caseData.gdq_status_date && caseData.gdq_status_id == 1) { // 1 is pending discharged
            const numberOfDays = 3
            const today = new Date()

            const compareDate = parseApiDateOrDateTime(caseData.gdq_status_date)
            if (!compareDate) return false
            const diffTime = Math.abs(today - compareDate)


            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
            if (diffDays >= numberOfDays) {
                return true
            }            
        }
        return false
    },
    workQListGrouped: (workQList, workQueueGroups) => {
        const findWorkQByGroupId = (workQId) =>
            workQList.find((item) => item.id == workQId)

        const visibleGroups = (workQueueGroups || []).filter(
            (g) => !(g.active == 0 || g.active === false)
        )

        // Group workQs based on group definitions and put ungrouped ones in an 'Undetermined' group
        const grouped = visibleGroups.map((group) => {
            const groupWorkQueues = Array.isArray(group.work_queues)
                ? group.work_queues
                : []
            return {
                ...group,
                workQs: groupWorkQueues
                    .map((workQId) => findWorkQByGroupId(workQId))
                    .filter(Boolean),
            }
        })

        const groupedWorkQIds = visibleGroups.flatMap((g) =>
            Array.isArray(g.work_queues) ? g.work_queues : []
        )

        const ungroupedWorkQs = workQList.filter(
            (wq) => !groupedWorkQIds.some((gid) => gid == wq.id)
        )

        if (ungroupedWorkQs.length > 0) {
            grouped.push({
                id: "undetermined",
                name: "Undetermined",
                workQs: ungroupedWorkQs,
            })
        }
        return grouped
    },
    isMedicalRecordQcellChanged: (data) => {
        if (data && data.mrq_last_upload_charge_flag) {
            return true
        }
        return false
    }
}